import {
  AdminCampaignProjectionDto,
  AdminCampaignProjectionDtoStatusEnum,
} from '@kortxio/hub-api';
import { statusToStatusAsChipLabel } from 'features/campaigns/helper';
import { ORDERED_CAMPAIGN_REPORT_METADATA } from 'features/reports/campaigns/helper';

export const adminCampaignPrivileges = [
  'CAMPAIGNS_READ',
  'REPORTS_READ',
  'CLIENTS_READ',
  'ADVERTISERS_READ',
];

export type AdminCampaignStatus = AdminCampaignProjectionDtoStatusEnum;

export const adminCampaignDealStages = [
  'Closed Lost',
  'Closed Won',
  'Prospect',
  'Proposal Sent',
  'Awaiting IO',
  'Pending Approval',
  'NA',
  'Received Verbal Confirmation',
] as const;

export type AdminCampaignRow = AdminCampaignProjectionDto;

export type AdminCampaignFilters = {
  advertiserIds: number[];
  clientIds: number[];
  dealStages: string[];
  reportNames: string[];
  search?: string;
  statuses: AdminCampaignStatus[];
};

const adminCampaignStatuses = Object.values(
  AdminCampaignProjectionDtoStatusEnum
);

export function isAdminCampaignStatus(
  value: string
): value is AdminCampaignStatus {
  return adminCampaignStatuses.some((status) => status === value);
}

export function toAdminCampaignStatuses(
  values: string[]
): AdminCampaignStatus[] {
  const statuses: AdminCampaignStatus[] = [];

  for (const value of values) {
    if (isAdminCampaignStatus(value)) {
      statuses.push(value);
    }
  }

  return statuses;
}

export function filterAdminCampaignRows(
  rows: AdminCampaignRow[],
  filters: AdminCampaignFilters
): AdminCampaignRow[] {
  const normalizedSearch = filters.search?.trim().toLowerCase();
  const normalizedReportNames = new Set(filters.reportNames);

  return rows.filter((row) => {
    if (normalizedSearch) {
      const matchesSearch =
        row.campaignName?.toLowerCase().includes(normalizedSearch) ||
        row.campaignId?.toString().includes(normalizedSearch);

      if (!matchesSearch) {
        return false;
      }
    }

    if (
      filters.clientIds.length > 0 &&
      (row.clientId === undefined || !filters.clientIds.includes(row.clientId))
    ) {
      return false;
    }

    if (
      filters.advertiserIds.length > 0 &&
      (row.advertiserId === undefined ||
        !filters.advertiserIds.includes(row.advertiserId))
    ) {
      return false;
    }

    if (
      filters.statuses.length > 0 &&
      (row.status === undefined || !filters.statuses.includes(row.status))
    ) {
      return false;
    }

    if (
      filters.dealStages.length > 0 &&
      (row.dealStageName === undefined ||
        !filters.dealStages.includes(row.dealStageName))
    ) {
      return false;
    }

    if (normalizedReportNames.size > 0) {
      const availableReportNames = row.availableReportNames ?? [];
      const availableReportNameSet = new Set(availableReportNames);
      const matchesAllReportNames = Array.from(normalizedReportNames).every(
        (reportName) => availableReportNameSet.has(reportName)
      );

      if (!matchesAllReportNames) {
        return false;
      }
    }

    return true;
  });
}

export function toEntityFilterOptions(
  rows: AdminCampaignRow[],
  field: 'clientId' | 'advertiserId',
  nameField: 'clientName' | 'advertiserName'
) {
  const options = new Map<number, { name: string; count: number }>();

  for (const row of rows) {
    const id = row[field];

    if (id === undefined) {
      continue;
    }

    const trimmedName = row[nameField]?.trim();
    const name =
      trimmedName === undefined || trimmedName === '' ? 'Unknown' : trimmedName;
    const existing = options.get(id);

    if (existing === undefined) {
      options.set(id, { name, count: 1 });
      continue;
    }

    options.set(id, { name: existing.name, count: existing.count + 1 });
  }

  return Array.from(options.entries())
    .map(([value, metadata]) => ({
      value,
      label: `${metadata.name} (${metadata.count})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function toStatusFilterOptions(rows: AdminCampaignRow[]) {
  const countsByStatus = new Map<AdminCampaignStatus, number>();

  for (const row of rows) {
    const { status } = row;

    if (status === undefined) {
      continue;
    }

    countsByStatus.set(status, (countsByStatus.get(status) ?? 0) + 1);
  }

  return adminCampaignStatuses.map((value) => {
    const statusLabel = statusToStatusAsChipLabel(value);
    const label = statusLabel === undefined ? value : statusLabel;
    const count = countsByStatus.get(value);

    return {
      value,
      label: `${label} (${count})`,
    };
  });
}

export function toAvailableReportFilterOptions(rows: AdminCampaignRow[]) {
  const countsByReportName = new Map<string, number>();

  for (const row of rows) {
    const reportNames = row.availableReportNames ?? [];
    const reportNameSet = new Set(reportNames);

    for (const reportName of reportNameSet) {
      countsByReportName.set(
        reportName,
        (countsByReportName.get(reportName) ?? 0) + 1
      );
    }
  }

  return Array.from(countsByReportName.entries())
    .sort((left, right) => left[0].localeCompare(right[0]))
    .map(([reportName, count]) => ({
      value: reportName,
      label: `${toReportLabel(reportName)} (${count})`,
    }));
}

export function toDealStageFilterOptions(rows: AdminCampaignRow[]) {
  const countsByDealStage = new Map<string, number>();

  for (const row of rows) {
    const dealStageName = row.dealStageName?.trim();

    if (dealStageName === undefined || dealStageName === '') {
      continue;
    }

    countsByDealStage.set(
      dealStageName,
      (countsByDealStage.get(dealStageName) ?? 0) + 1
    );
  }

  return adminCampaignDealStages.map((value) => ({
    value,
    label: `${value} (${countsByDealStage.get(value)})`,
  }));
}

export function toValidReportNames(
  reportNames: string[],
  options: ReadonlyArray<{ value: string }>
) {
  const validReportNames = new Set(options.map((option) => option.value));
  return reportNames.filter((reportName) => validReportNames.has(reportName));
}

export function groupByParentId<T extends { parentId?: number }>(
  items: T[]
): Map<number, T[]> {
  const map = new Map<number, T[]>();
  for (const item of items) {
    if (item.parentId == null) {
      continue;
    }
    const list = map.get(item.parentId) ?? [];
    list.push(item);
    map.set(item.parentId, list);
  }
  return map;
}

function toReportLabel(reportName: string): string {
  const reportMetadata =
    ORDERED_CAMPAIGN_REPORT_METADATA[
      reportName as keyof typeof ORDERED_CAMPAIGN_REPORT_METADATA
    ];

  return reportMetadata?.label ?? reportName;
}
